import { BehaviorSubject, Observable, Subject, merge } from 'rxjs';
import { map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';

import { MxEventName } from '@axon-enterprise/track';
import { SnCallSignsAPI } from '@lib/api/services';
import { SnCurrentUser } from '@lib/auth/services';
import { snZoomIn, snZoomOut } from '@lib/common/animations';
import { ISnComponentOrTemplateConfig } from '@lib/common/interfaces';
import { SnScreenService, SnViewport } from '@lib/common/services';
import { SnComponentOrTemplate } from '@lib/common/types';
import { MixpanelService } from '@lib/mixpanel';
import { SnRouterHelperService } from '@lib/state/router';
import { SnTitleService } from '@lib/state/title';
import { CallSignDialogComponent } from '@lib/teams/components/call-sign-dialog/call-sign-dialog.component';
import { SnActionConfig } from '@lib/ui/actions';
import { SnSearchbarOptionConfig } from '@lib/ui/searchbar';
import { SnUsersAPI } from 'libs/api/services/src/lib/users.service';

import { SnLayoutAppbarConfig } from '../../models';
import { SnLayoutAppbarReference } from '../../references';
import { SnLayoutService, SnLayoutStorageService } from '../../services';
import { SnLayoutAppbarMode } from '../../types';

@Component({
  selector: 'sn-layout-appbar',
  templateUrl: './layout-appbar.container.html',
  styleUrls: ['./layout-appbar.container.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [snZoomIn, snZoomOut],
  host: {
    class: 'sn-layout-appbar',
  },
})
export class SnLayoutAppbarContainer implements OnInit, AfterViewInit, OnDestroy {
  @Input() public hideAlertBoundary: boolean = false;

  get actions$(): Observable<SnActionConfig[]> {
    return this._reference.actions.change$;
  }

  get actionsComponentOrTemplate$(): Observable<SnComponentOrTemplate> {
    return this._reference.actions.componentOrTemplate$;
  }

  get actionsComponentOrTemplateConfig$(): Observable<ISnComponentOrTemplateConfig> {
    return this._reference.actions.componentOrTemplateConfig$;
  }

  get searchValue$(): Observable<string> {
    return this._reference.search.change$;
  }

  get searchOptions$(): Observable<SnSearchbarOptionConfig[]> {
    return this._reference.search.options$;
  }

  get navButtonVisible$(): Observable<boolean> {
    return this._reference.navButton.visibleChange$;
  }

  get mode(): SnLayoutAppbarMode {
    return this._reference.mode;
  }

  get callSign(): string {
    return this._currentUser.callSign;
  }

  get hasBoundary(): boolean {
    return (
      this._currentUser?.settings?.mapSetting?.alertsBoundary || !!this._currentUser.alertBoundary
    );
  }

  get title$(): Observable<string> {
    return this._titleService.getTitle$();
  }

  @HostBinding('class.sn-layout-appbar--fixed')
  get isFixed(): boolean {
    return this.mode === 'fixed';
  }

  @HostBinding('class.sn-layout-appbar--collapsed')
  get isCollapsed(): boolean {
    return this.mode === 'collapsed';
  }

  @HostBinding('class.sn-layout-appbar--hidden')
  get isHidden(): boolean {
    return !this._reference.isVisible;
  }

  @HostBinding('class.sn-layout-appbar--two-row')
  get twoRow(): boolean {
    return this._twoRow;
  }

  public readonly searchActive$: Observable<false | 'bottom' | 'top'>;

  @HostBinding('class')
  public appbarClass: string;

  private readonly _areCallsignsAvailable = new BehaviorSubject<boolean | undefined>(undefined);
  public areCallsignsAvailable$: Observable<boolean | undefined> =
    this._areCallsignsAvailable.asObservable();

  private readonly _reference: SnLayoutAppbarReference;

  private _twoRow: boolean = false;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    activatedRoute: ActivatedRoute,
    routerHelper: SnRouterHelperService,
    config: SnLayoutAppbarConfig,
    public layout: SnLayoutService,
    private readonly _callSignService: SnCallSignsAPI,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _screen: SnScreenService,
    private readonly _currentUser: SnCurrentUser,
    private readonly _usersAPI: SnUsersAPI,
    private readonly _layoutStorage: SnLayoutStorageService,
    private readonly _matDialog: MatDialog,
    private readonly _mixpanelService: MixpanelService,
    private readonly _router: Router,
    private readonly _titleService: SnTitleService,
  ) {
    this._reference = new SnLayoutAppbarReference(activatedRoute, routerHelper, config);
    const viewportChange$ = this._screen.viewportChange$.pipe(
      startWith(this._screen.viewport),
      takeUntil(this._destroy$),
    );

    this.searchActive$ = merge(viewportChange$, this._reference.search.activationChange$).pipe(
      map(() => {
        if (this._reference.search.isActive) {
          return this._screen.viewport === SnViewport.Handset ? 'bottom' : 'top';
        }

        return false;
      }),
    );

    const userHasCallSign = this._currentUser.callSign;

    if (userHasCallSign) {
      this._areCallsignsAvailable.next(true);
    } else {
      this._callSignService
        .getCallSignList({ page: 1, pageSize: 1 })
        .pipe(
          map((response) => response.results),
          tap((callSigns) => this._areCallsignsAvailable.next(callSigns.length > 0)),
          takeUntil(this._destroy$),
        )
        .subscribe();
    }
  }

  public ngOnInit(): void {
    this._layoutStorage.checkInAppbar(this._reference);
    this._watchMode();
    this._watchClass();
  }

  public ngAfterViewInit(): void {
    this.onToolBarMouseEnterleave();
  }

  public ngOnDestroy(): void {
    this._reference.onDestroy();
    this._layoutStorage.checkOfAppbar();

    this._destroy$.next();
    this._destroy$.complete();
  }

  public onSearch(value: string): void {
    this._reference.search.onSearch(value);
  }

  public toggleCollapsed(status: boolean): void {
    this.layout.lockAppbar = status;
    this._updateUserSettings();
  }

  public onSearchClick(): void {
    if (this.layout.isOnMap) {
      this._mixpanelService.trackMetric(MxEventName.SearchMapStartAf, {});
    }
  }

  public onToolBarMouseEnterleave(): void {
    const expandToolbar = document.getElementById('fs_main-toolbar');
    expandToolbar.addEventListener('mouseenter', () => {
      this._onToolBarHoverChange(true, expandToolbar);
    });
    expandToolbar.addEventListener('mouseleave', () => {
      this._onToolBarHoverChange(false, expandToolbar);
    });
  }

  public viewAlarmBoundary(): void {
    this.layout.alarmBoundary = true;
    if (!this._router.url.includes('/map')) {
      this._router.navigate(['/map']);
    }
  }

  public openCallSignDialog(): void {
    const dialogRef = this._matDialog.open(CallSignDialogComponent, {
      width: '100%',
      maxWidth: '500px',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((callSign) => {
        if (callSign) {
          this._changeCallSign(callSign);
        }
      });
  }

  private _onToolBarHoverChange(onEnter: boolean, toolbar: HTMLElement): void {
    if (this.layout.lockAppbar) {
      return;
    }
    if (onEnter) {
      toolbar.style.marginTop = '0px';
      this._collapseMapDrawer(true);
      this._changeDetectorRef.detectChanges();
    } else {
      toolbar.style.marginTop = '-64px';
      this._collapseMapDrawer(false);
      this._changeDetectorRef.detectChanges();
    }
  }

  private _collapseMapDrawer(collapsed: boolean): void {
    const activeDrawer: HTMLElement = document.querySelector('.fs_map-drawer');
    if (!activeDrawer) {
      return;
    }
    if (collapsed) {
      activeDrawer.style.height = 'calc(100vh - 64px)';
    }
    if (!collapsed) {
      activeDrawer.style.height = '100vh';
    }
  }

  public onSearchSubmit(value: string): void {
    this._reference.search.onSubmit(value);

    if (this.layout.isOnMap) {
      this._mixpanelService.trackMetric(MxEventName.SearchMapAf, {});
    }
  }

  private _changeCallSign(callSign: string): void {
    this._usersAPI
      .changeCallSign(this._currentUser.id, callSign)
      .pipe(
        switchMap(() => this._currentUser.getProfile()),
        takeUntil(this._destroy$),
      )
      .subscribe({
        next: () => {
          this._currentUser.callSign = this.callSign;
          this._currentUser.callsignChanged();
          this._changeDetectorRef.detectChanges();
        },
      });
  }

  private _onModeChange(mode: SnLayoutAppbarMode): void {}

  private _watchMode(): void {
    this._reference.modeChange$.pipe(takeUntil(this._destroy$)).subscribe((mode) => {
      this._onModeChange(mode);
    });
  }

  private _watchClass(): void {
    this._reference.classChange$
      .pipe(takeUntil(this._destroy$))
      .subscribe((res) => (this.appbarClass = res));
  }

  private _updateUserSettings(): void {
    this._currentUser
      .updateSettings({
        mapSetting: {
          ...this._currentUser.settings.mapSetting,
          lockToolbar: this.layout.lockAppbar,
        },
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe();
  }
}
